"use client";

import { getLink } from "@utils/linkUtils";
import { getCookie, setCookie } from "cookies-next";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/dist/client/components/navigation";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import Accordion from "@/components/Accordion/Accordion";
import AccordionItem from "@/components/Accordion/AccordionItem";
import DeviceStore from "@/components/DeviceStore/DeviceStore";
import NewsletterForm from "@/components/NewsletterForm/NewsletterForm";
import FooterSocial from "@/components/Social/FooterSocial";
import AnchorLink from "@/components/ui/AnchorLink/AnchorLink";
// import Image from "next/image";
import Image from "@/components/ui/Image/Image";
import { TemplateContext } from "@/context/TemplateContext";
import { UTMParametersContext, getFallbackCampaignParameters } from "@/context/UTMParametersContext";
import { footerLinkTracking, policyTracking, projectPageViewEvent } from "@/dataLayer/dataLayer";
import useBreakPoint from "@/hooks/useBreakpoint";
import useClientInfo from "@/hooks/useClientInfo";
import useMediaQuery from "@/hooks/useMediaQuery";
import { CustomLink } from "@/types";
import { transparentPixel } from "@/utils/string";

import "./footer.css";
import { FooterProps } from "./types";

interface StoreLink extends CustomLink {
  currentDevice: boolean;
}
export default function Footer({
  damacLogo,
  damacLivingLogo,
  damacLivingAppleLogo,
  damacLivingGoogleLogo,
  pageClusters,
  legalPages,
  stores: deviceStores,
  socialChannels,
  copyright,
  userAgent,
  newsLetterFormHyperLink,
  damacLivingLogoArabic,
}: FooterProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { isMd } = useBreakPoint("md");
  const client = useClientInfo({ userAgent });
  const pathname = usePathname();
  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(isMd);
  const [stores, setStores] = useState<StoreLink[]>([]);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const { utmParameters } = useContext(UTMParametersContext);
  const [pageTitle, setPageTitle] = useState("");
  const [whatsFinalUrl, setWhatsFinalURL] = useState<{ finalLandingPageURL: string; searchParams: URLSearchParams }>();
  const userStorageLanguage = getCookie("country")?.toLowerCase();
  const countryJeo = getCookie("countryJeo")?.toLowerCase();
  const [isSo, setIsSo] = useState<boolean>(false);

  // Retrieves parameters from the localStorage and converts them into query strings
  const getLocalStorageParams = () => {
    if (typeof window !== "undefined") {
      const params = localStorage.getItem("utmParameters");
      if (params) {
        const parsedParams = JSON.parse(params);
        const searchParams = new URLSearchParams(parsedParams);
        return searchParams.toString();
      }
    }
  };

  // Gets and set UTM Parameters
  const parameters: { [key: string]: string } = {
    ...getFallbackCampaignParameters(utmParameters?.utm_medium),
    ...utmParameters,
  };

  const whatsappURL = useMemo(() => {
    if (typeof window !== "undefined") {
      // Get base URL without query parameters
      const currentUrl = new URL(window.location.href);
      const baseUrl = `${currentUrl.origin}${currentUrl.pathname}`;

      // Parse existing URL parameters
      const currentParams = new URLSearchParams(currentUrl.search);

      // Get localStorage parameters
      const localStorageParams = getLocalStorageParams();

      // Properly type the object with index signature
      const localStorageParamsObj: Record<string, string> = {};

      if (localStorageParams) {
        // Convert localStorageParams string to object
        const searchParams = new URLSearchParams(localStorageParams);
        searchParams.forEach((value, key) => {
          localStorageParamsObj[key] = value;
        });
      }

      // Create a new URLSearchParams instance for the final parameters
      const finalParams = new URLSearchParams();

      // Priority: localStorage parameters over URL parameters
      // Add current URL parameters first (lower priority)
      currentParams.forEach((value, key) => {
        finalParams.append(key, value);
      });

      // Then override with localStorage parameters (higher priority)
      Object.entries(localStorageParamsObj).forEach(([key, value]) => {
        finalParams.set(key, String(value)); // Explicitly convert to string
      });

      // Construct the final landing page URL - CHECK IF PARAMS EXIST TO AVOID ?&
      const paramsString = finalParams.toString();
      const finalLandingPageURL = paramsString ? `${baseUrl}?${paramsString}` : baseUrl;

      // Create search params from campaign parameters
      const searchParams = new URLSearchParams(
        parameters["campaign_id"] === "a120Y000000uLMj" ? { campaign_id: parameters["campaign_id"] } : parameters,
      );
      searchParams.delete("fullLandingPageUrl");
      searchParams.delete("landingPageURL");

      // Check if the URL already contains utm_medium or campaign_id
      // Parse the finalLandingPageURL to check for duplicate parameters
      const finalUrlObj = new URL(finalLandingPageURL);
      const finalUrlParams = finalUrlObj.searchParams;

      // When constructing the final WhatsApp URL text, check if parameters already exist
      const textUrlParams = new URLSearchParams(searchParams.toString());

      // Specifically check and remove duplicates for utm_medium and campaign_id
      if (finalUrlParams.has("utm_medium") && textUrlParams.has("utm_medium")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("utm_medium");
      }

      if (finalUrlParams.has("campaign_id") && textUrlParams.has("campaign_id")) {
        // Keep the one from finalUrlParams (from localStorage/URL) and remove from textUrlParams
        textUrlParams.delete("campaign_id");
      }

      return {
        finalLandingPageURL,
        searchParams: textUrlParams,
      };
    }
    return undefined;
  }, [parameters]);
  const whatsappNumber = t("label.whatsapp.number") || "97144016335";

  // Takes the name of the property from the metadata and sets the label that is sent with the WhatsApp message
  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window?.location?.pathname?.includes("projects")) {
        const title = document.title;
        setPageTitle(title);
      }
    }
  }, []);

  useEffect(() => {
    if (deviceStores && deviceStores?.length > 0) {
      const os = client?.getOS()?.name?.toLowerCase();
      const stores = (deviceStores as StoreLink[]).map((item) => {
        item.currentDevice = false;
        if (!!os) {
          // we only need to test if it an iphone
          if (new RegExp(/apple/i).test(item.label)) {
            if (["mac os", "ios"].includes(os)) {
              item.currentDevice = true;
            }
          } else if (["android"].includes(os)) {
            item.currentDevice = true;
          }
        }
        return item;
      });
      setStores(stores);
    }
  }, [deviceStores, client, userAgent, isMd]);

  useEffect(() => {
    setIsExpanded(isMd);
  }, [isMd]);

  useEffect(() => {
    // This code will only run on the client-side
    if (typeof window !== "undefined") {
      const value = localStorage.getItem("isSo");
      if (value) {
        setIsSo(value === "true");
      }
    }
  }, []);

  return isSo ? null : (
    <footer className="footer w-full z-20">
      <div className="px-m md:container">
        <div className="footer__content">
          {/* LOGO */}
          <div className="footer-logo">
            <div className="flex items-center justify-center h-full md:justify-normal">
              <AnchorLink href={`/${locale}/`} aria-label={t("button.label.homepage")}>
                <Image
                  className="m-auto max-w-[170px] md:m-0 "
                  src={damacLogo?.link?.image?.url || transparentPixel}
                  width={170}
                  height={90}
                  alt={damacLogo?.link?.image?.description || damacLogo?.link?.image?.name || ""}
                  fit="pad"
                />
              </AnchorLink>
            </div>
          </div>
          {/* SOCIAL */}
          <div className="footer-social-links" style={{ paddingRight: `${locale?.split("-")?.[0] === "ar" ? "10px" : "48px"}` }}>
            <p className="text-s mt-m mb-s md:hidden">{t("label.common.followUs")}</p>
            <div className="flex flex-row items-center justify-center md:flex-row md:justify-start h-l md:h-full gap-m">
              {socialChannels?.map((social, i) => (
                <FooterSocial
                  key={`footer-social-${social.id}`}
                  label={social.label}
                  src={`${social.link?.icon}`}
                  url={
                    social.link.url.includes("api.whatsapp.com")
                      ? !!whatsappURL
                        ? `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${
                            pathname?.includes("projects")
                              ? encodeURIComponent(
                                  t("text.project.pages", {
                                    projectName: pageTitle,
                                    url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                                  }),
                                )
                              : encodeURIComponent(
                                  t("text.generic.pages", {
                                    url: `${whatsappURL?.finalLandingPageURL}${whatsappURL?.finalLandingPageURL?.includes("?") ? "&" : "?"}${whatsappURL?.searchParams}`,
                                  }),
                                )
                          }`
                        : t("text.generic.pages", {
                            url: `https://api.whatsapp.com/send?phone=${whatsappNumber}&campaign_id=${parameters["campaign_id"]}`,
                          })
                      : social.link.url
                  }
                />
              ))}
            </div>
          </div>
          {/* NEWSLETTER */}
          <div className="footer-newsletter">
            <NewsletterForm newsLetterFormHyperLink={newsLetterFormHyperLink} />
          </div>
          {/* LINKS ACCORDION*/}
          <div className={`footer-links ${locale?.split("-")?.[0] === "ar" ? "md:mr-xl" : ""}`}>
            <div className="w-full only-on-mobile md:flex md:gap-xl md:py-l">
              <div className="flex flex-wrap">
                {pageClusters?.map((item, index) => (
                  <Accordion key={`footer-group${index}`} classes={`text-s ${isDesktop ? "min-w-[243px]" : "w-full"} md:min-w-max invert md:ml-xxl`}>
                    <AccordionItem
                      tag="div"
                      title={item.title}
                      innerTag="h3"
                      accordionId={`accordion-${index}`}
                      expanded={isExpanded}
                      useButton={!isExpanded}
                    >
                      <ul className="px-0 pb-0 mb-xs md:pb-m md:pt-xs">
                        {item.pages?.map((page) => (
                          <li
                            className="py-m font-secondary-light text-m md:text-s md:py-xxs"
                            key={`footer-pages-${page.id}`}
                            onClick={() => {
                              footerLinkTracking(item.title, page.label);
                              // if (item.title === "COMMUNITIES" || item.title === "FEATURED PROJECTS") {
                              //   projectPageViewEvent(page.label);
                              // }
                            }}
                          >
                            <AnchorLink className="hover:underline underline-offset-4" href={getLink(page, locale).toString()}>
                              {page.label}
                            </AnchorLink>
                          </li>
                        ))}
                      </ul>
                    </AccordionItem>
                  </Accordion>
                ))}
              </div>
            </div>
          </div>
          {/* DEVICE STORE */}
          <div className="footer-device-store">
            <div className="flex flex-col items-center w-full pt-m md:pt-0">
              {damacLivingLogo && (
                <AnchorLink href={damacLivingLogo?.link?.url || ""}>
                  <Image
                    className="self-center"
                    src={
                      locale === "ar"
                        ? damacLivingLogoArabic?.logoImage?.url || transparentPixel
                        : damacLivingLogo?.link?.image?.url || transparentPixel
                    }
                    width={170}
                    height={80}
                    fit="pad"
                    alt={damacLivingLogo?.link?.image?.description || damacLivingLogo?.link?.image?.name || ""}
                  />
                </AnchorLink>
              )}
              <div className="flex flex-row justify-center flex-wrap max-w-[270px] gap-m mt-m mb-m">
                {stores?.map((store, index) => {
                  const ImageUrl =
                    locale === "ar"
                      ? store.label === "Damac Living Apple"
                        ? damacLivingAppleLogo?.logoImage?.url || `${store?.link?.image?.url}`
                        : damacLivingGoogleLogo?.logoImage?.url || `${store?.link?.image?.url}`
                      : `${store?.link?.image?.url}`;
                  return (
                    <div key={`store-${index}`} className={`${store.currentDevice ? "" : "hidden md:block"}`}>
                      <DeviceStore img={ImageUrl || ""} url={store?.link?.url} alt={store?.label} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {/* BOTTOM LINKS */}
          <div className="footer-bottom">
            <div className="footer-bottom-links">
              {legalPages && (
                <ul className="footer-bottom-link-items">
                  {legalPages.map((item) => (
                    <li key={`footer-bottom-link-${item.id}`}>
                      <AnchorLink href={getLink(item, locale).toString()} onClick={() => policyTracking(item.label)}>
                        {item.label}
                      </AnchorLink>
                    </li>
                  ))}
                </ul>
              )}
              <p>
                {copyright}
                {` `}
                <small>({`v${process.env?.VERSION ?? ""}`})</small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
